import type { RuleObject, RuleRender } from "antd/es/form"
import type { NamePath } from "antd/es/form/interface"
import { validationMessage } from "../common/common"
import { AlphabetsRegex, ArabicAlphabetsRegex, ArabicNameFieldRegex, BankAccountRegex, EmailRegex, FiveDigitsWithNumericRegex, FourDigitsRegex, IqamaNumberRegex, IBANRegex, IdentityNumberRegex, NameFieldAllLettersRegex, NameFieldRegex, NumericRegex, NumericTwoDecimalRegex, NumericTwoDecimalWithZeroRegex, NumericWithComma, NumericWithDecimalRegex, NumericWithSpecialCharacter, NumericWithTwoDecimalRegex, PhoneNumberRegex, SWIFTRegex, SaudiPlateNumberRegex, ThreeDigitsRegex, TwoDigitDecimalWithOneDigitPrecisionRegex, TwoDigitsRegex, oneAlphaCodeRegex, vehicleTypeFieldRegex, remarksLengthRegex, PositiveNonZeroDecimalNumberRegex, passwordRegex, markenHAWBLengthRegex, NumericRegexNotAllowingZero, ShortAddressRegex, twoAlphaCodeRegex, contactNameRegex, numericInputRegex } from "../utils/regex"

export const requiredValidation = {
    required: true,
    message: validationMessage.REQUIRED
}

export const oneAlphaCodeValidation = {
    pattern: oneAlphaCodeRegex,
    message: validationMessage.INVALID_ONE_ALPHA_CODE
}


export const twoAlphaCodeValidation = {
    pattern: twoAlphaCodeRegex,
    message: validationMessage.INVALID_TWO_ALPHA_CODE
}

export const alphabeticValidation = {
    pattern: AlphabetsRegex,
    message: validationMessage.ALPHABETS_TEXT
}

export const numericInputValidation = {
    pattern: numericInputRegex,
    message: validationMessage.INVALID_TEXT
}



export const FourDigitsNumericValidation = {
    pattern: FourDigitsRegex,
    message: validationMessage.INVALID_FOUR_DIGITS_NUMBER
}

export const FiveDigitsNumericWithDecimalsValidation = {
    pattern: FiveDigitsWithNumericRegex,
    message: validationMessage.INVALID_FOUR_DIGITS_NUMBER
}

export const TwoDigitsNumericValidation = {
    pattern: TwoDigitsRegex,
    message: validationMessage.INVALID_TWO_DIGITS_NUMBER
}

export const TwoDigitDecimalWithOneDigitPrecisionValidation = {
    pattern: TwoDigitDecimalWithOneDigitPrecisionRegex,
    message: validationMessage.INVALID_TWO_DIGIT_DECIMAL_WITH_ONE_DIGIT_PRECISION
}

export const ThreeDigitsNumericValidation = {
    pattern: ThreeDigitsRegex,
    message: validationMessage.INVALID_THREE_DIGITS_NUMBER
}

export const numericValidation = {
    pattern: NumericRegex,
    message: validationMessage.INVALID_TEXT
}
export const numericNotZeroValidation = {
    pattern: NumericRegexNotAllowingZero,
    message: validationMessage.INVALID_TEXT
}
export const ArabicAlphabeticValidation = {
    pattern: ArabicAlphabetsRegex,
    message: validationMessage.ARABIC_ALPHABETS_TEXT
}

export const numericDecimalValidation = {
    pattern: NumericWithDecimalRegex,
    message: validationMessage.INVALID_TEXT
}
export const numericTwoDecimalValidation = {
    pattern: NumericTwoDecimalRegex,
    message: validationMessage.INVALID_2_DIGIT_DECIMAL_NUMBER
}

export const isEmptyValue = (value: any) => value === undefined || value === null || String(value).trim() === '';

export const toNullableNumber = (value: any): number | null => (isEmptyValue(value) ? null : +value);

// Returns an error message when value is not a strictly positive number with at most maxDecimals decimals
// (no whitespace, no sign, no zero, no exponent or stray dots), otherwise undefined. Empty values are left to requiredValidation.
const getPositiveNumberError = (label: string, value: any, maxDecimals: number): string | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const text = String(value);
    if (/\s/.test(text)) {
        return `${label} must not contain spaces`;
    }
    if (text.startsWith('-')) {
        return `${label} cannot be negative`;
    }
    if (!new RegExp(`^[0-9]+([.][0-9]{1,${maxDecimals}})?$`).test(text)) {
        return `${label} must be a valid number (max ${maxDecimals} decimals)`;
    }
    if (Number(text) <= 0) {
        return `${label} must be greater than 0`;
    }
    return undefined;
};

// Strictly positive number (> 0), no whitespace, up to maxDecimals decimal places.
export const positiveNumberValidation = (label: string, maxDecimals = 3): RuleObject => ({
    validator: (_, value) => {
        const error = getPositiveNumberError(label, value, maxDecimals);
        return error ? Promise.reject(new Error(error)) : Promise.resolve();
    },
});

// Range "From": required is handled by requiredValidation; this checks format and that it is > 0.
export const rangeFromValidation: RuleObject = positiveNumberValidation('From');

// Range "To": optional; when provided it must be > 0 and > the "From" value at fromPath.
// Pair with `dependencies={[fromPath]}` so it re-validates when "From" changes.
export const rangeToValidation = (fromPath: NamePath): RuleRender => ({ getFieldValue }) => ({
    validator: (_, value) => {
        const error = getPositiveNumberError('To', value, 3);
        if (error) {
            return Promise.reject(new Error(error));
        }
        const from = getFieldValue(fromPath);
        if (!isEmptyValue(value) && !getPositiveNumberError('From', from, 3) && !isEmptyValue(from) && Number(value) <= Number(from)) {
            return Promise.reject(new Error('To must be greater than From'));
        }
        return Promise.resolve();
    },
})

export type RangeBounds = { from: number; to: number | null };

// Ranges are treated as [from, to): touching ranges (1-20 and 20-50) are allowed, any real overlap is not.
// An empty "To" means open-ended (infinity), so 10-∞ only leaves room for ranges ending at or before 10.
export const isRangeOverlapping = (a: RangeBounds, b: RangeBounds): boolean =>
    a.from < (b.to ?? Infinity) && b.from < (a.to ?? Infinity);

export const formatRange = ({ from, to }: RangeBounds): string => `${from} - ${to ?? '∞'}`;

// Parses raw From/To input into bounds; null when the pair is incomplete or invalid (those cases are reported by
// rangeFromValidation / rangeToValidation, so overlap checks simply skip them).
export const parseRangeBounds = (from: any, to: any): RangeBounds | null => {
    if (isEmptyValue(from) || getPositiveNumberError('From', from, 3) || getPositiveNumberError('To', to, 3)) {
        return null;
    }
    const bounds = { from: Number(from), to: toNullableNumber(to) };
    return bounds.to !== null && bounds.to <= bounds.from ? null : bounds;
};

export type RangeOverlapCandidate = RangeBounds & { source: string };

// Range "From": rejects when [From, To) overlaps any candidate returned by getCandidates (e.g. saved ranges of the
// same group, other rows of the same form). Pair with `dependencies` on toPath and on every field getCandidates reads.
export const rangeOverlapValidation = (
    toPath: NamePath,
    getCandidates: (getFieldValue: (name: NamePath) => any) => RangeOverlapCandidate[],
): RuleRender => ({ getFieldValue }) => ({
    validator: (_, value) => {
        const bounds = parseRangeBounds(value, getFieldValue(toPath));
        const conflict = bounds && getCandidates(getFieldValue).find((candidate) => isRangeOverlapping(bounds, candidate));
        return conflict
            ? Promise.reject(new Error(`Overlaps ${conflict.source} ${formatRange(conflict)}`))
            : Promise.resolve();
    },
})

export const numericTwoDecimalWithZeroValidation = {
    pattern: NumericTwoDecimalWithZeroRegex,
    message: validationMessage.INVALID_2_DIGIT_DECIMAL_NUMBER
}
export const numericWithTwoDecimalValidation = {
    pattern: NumericWithTwoDecimalRegex,
    message: validationMessage.INVALID_2_DIGIT_DECIMAL_NUMBER
}


export const phoneNumberCheck = {
    pattern: PhoneNumberRegex,
    message: validationMessage.INVALID_PHONE_NUMBER
}
export const phoneNumberValidation = [
    requiredValidation,
    phoneNumberCheck
]

export const emailCheck = {
    pattern: EmailRegex,
    message: validationMessage.INVALID_EMAIL_ADDRESS
}

export const emailValidation = [
    requiredValidation,
    emailCheck,
]

export const postalValidation = [
    requiredValidation,
    numericValidation,
]

export const bankAccountValidation = [
    requiredValidation,
    {
        pattern: BankAccountRegex,
        message: validationMessage.INVALID_BANK_ACCOUNT
    },
]

export const bankSWIFTCodeValidation = [
    requiredValidation,
    {
        pattern: SWIFTRegex,
        message: validationMessage.INVALID_SWIFT
    },
]
export const bankIBANCodeValidation = [
    requiredValidation,
    {
        pattern: IBANRegex,
        message: validationMessage.INVALID_IBAN
    },
]

export const numericFieldValidation = [
    requiredValidation,
    numericValidation,
]

export const numericFieldWithCommaValidation = {
    pattern: NumericWithComma,
    message: validationMessage.INVALID_TEXT
}

export const contactNameValidation = {
    pattern: contactNameRegex,
    message: validationMessage.INVALID_CONTACT_NAME
}


export const referenceLengthValidation = {
    max: 70,
    message: validationMessage.REFERENCE_NUMBER_LENGTH
}
export const googleMapLengthValidation = {
    max: 150,
    message: validationMessage.MAP_NUMBER_LENGTH
}
export const amendAmountValidation = {
    pattern: NumericWithSpecialCharacter,
    message: validationMessage.INVALID_TEXT_WITH_SPECIAL_CHARACTER
}

export const IdentityLengthNumber = {
    max: 10,
    min: 10,
    pattern: IdentityNumberRegex,
    message: validationMessage.IDENTITY_NUMBER_LIMIT
}
export const IssueNumberLengthValidation = {
    max: 3,
    min: 1,
    message: validationMessage.ISSUE_NUMBER_LIMIT
}

export const ComplaintSubjectLengthValidation = {
    max: 45,
    min: 1,
    message: validationMessage.SUBJECT_COMPLAINT_LENGTH
}

export const RemarksLengthValidation = {
    max: 250,
    message: validationMessage.EXCEPTION_REMARKS_LENGTH
}

export const saudiPlateNumberValidation = {
    pattern: SaudiPlateNumberRegex,
    message: validationMessage.INVALID_SAUDI_PLATE_NUMBER
}

export const nameFieldValidation = {
    pattern: NameFieldRegex,
    message: validationMessage.NAME_FIELD
}

export const arabicNameFieldValidation = {
    pattern: ArabicNameFieldRegex,
    message: validationMessage.ARABIC_NAME_FIELD
}

export const vehicleTypeFieldValidation = {
    max: 50,
    min: 3,
    pattern: vehicleTypeFieldRegex,
    message: validationMessage.VEHICLE_TYPE_FIELD
}

export const NameFieldAllLettersValidation = {
    pattern: NameFieldAllLettersRegex,
    message: validationMessage.ALL_LETTERS_NAME_FIELD
}

export const IqamaNumberValidation = {
    pattern: IqamaNumberRegex,
    message: validationMessage.IQAMA_NUMBER
}
export function validateNumberInput(event) {
    const key = event.key;
    const inputValue = event.target.value;

    // Prevent entering 'e', 'E', '-', or '+'
    if (['e', 'E', '-', '+'].includes(key)) {
        event.preventDefault();
        return;
    }

    // Allow only numeric values and a single decimal point
    if (!/[\d.]/.test(key)) {
        event.preventDefault();
        return;
    }

    // Allow only one decimal point
    if (key === '.') {
        // Check if there's already a decimal point in the input value
        if (inputValue.includes('.')) {
            event.preventDefault();
            return;
        }
    }

    // Prevent entering more than two decimal places
    const [integerPart, decimalPart] = (inputValue + key).split('.');
    if (decimalPart && decimalPart.length > 2) {
        event.preventDefault();
    }
}

export const validateDistinctMobileNumbers = (getFieldValue: any) => ({
    validator(_: any, value: string) {
        if (!value || getFieldValue('mobileNumber1') !== value) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Consignee Number 2 should not be the same as Consignee Number 1'));
    },
});

// commonValidators.js
export const maxCountRule = (maxCount = 1500, name = '') => ({
    validator: (_, value) => {
        if (value > maxCount) {
            return Promise.reject(`${name} cannot be more than ${maxCount}`);
        }
        return Promise.resolve();
    },
});

export const remarksLengthValidation = {
    pattern: remarksLengthRegex,
    message: validationMessage.EXCEPTION_REMARKS_LENGTH
}

export const PositiveNonZeroDecimalNumberValidation = {
    pattern: PositiveNonZeroDecimalNumberRegex,
    message: validationMessage.INVALID_POSITIVE_NON_ZERO_DECIMAL_NUMBER
}

export const decimalNumberMaxLengthValidation = {
    validator(_, value) {
        const trimmedValue = value?.trim();
        if (!trimmedValue || /^(0|[1-9]\d{0,6})(\.\d{0,3})?$/.test(trimmedValue)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error(`Must be at most 7 digits with at most 3 decimal places`));
    },
};

export const markenHAWBLengthValidation = {
    pattern: markenHAWBLengthRegex,
    message: validationMessage.MARKEN_HAWB_LENGTH
}
export const passwordValidation = {
    pattern: passwordRegex,
    message: validationMessage.PASSWORD_COMPLEXITY
}

export const ShortAddressValidation = [
    {
        pattern: ShortAddressRegex,
        message: validationMessage.INVALID_SHORT_ADDRESS
    },
]

export const alphabeticSentenceValidation = {
    pattern: /^[A-Za-z\s]+$/,
    message: validationMessage.ALPHABETS_TEXT
}

export const ArabicSentencesValidation = {
    pattern: /^[\u0621-\u064A0-9\s!@#$%^&*()_+\-=\[\]{};:'",.<>/?\\|`~]+$/,
    message: validationMessage.ARABIC_ALPHABETS_TEXT
}

export const PostalCodeValidation = {
    pattern: /^\d{5}$/,
    message: 'Postal code must be a 5-digit number'
};

export const VATValidation = {
    pattern: /^3\d{13}3$/,
    message: 'VAT Registration Number must be a 15-digit number starting and ending with the digit "3"'
}
export const timeRangeValidation = (minDuration = 15) => ({
    validator: (_, value) => {
        if (!value) {
            return Promise.resolve();
        }
        if (!Array.isArray(value) || value.length !== 2) {
            return Promise.reject(new Error('Please select both start and end time'));
        }
        const [startTime, endTime] = value;
        if (!startTime || !endTime) {
            return Promise.reject(new Error('Both start and end times are required'));
        }
        if (startTime.isSameOrAfter(endTime)) {
            return Promise.reject(new Error('End time must be after start time'));
        }
        const duration = endTime.diff(startTime, 'minutes');
        if (duration < minDuration) {
            return Promise.reject(new Error(`Time window must be at least ${minDuration} minutes`));
        }
        return Promise.resolve();
    },
});

